import React, { useState, useEffect, useCallback, useTransition } from 'react';
import type {
  Vocabulary,
  VocabularyGroup,
  CreateVocabularyDTO,
  UpdateVocabularyDTO,
} from '../types/vocabulary.types';
import { vocabularyService } from '../services/vocabulary.service';
import { vocabularyGroupService } from '../services/vocabulary-group.service';
import { VocabularyCard } from '../components/vocabulary/VocabularyCard';
import { VocabularyFormModal } from '../components/vocabulary/VocabularyFormModal';
import { DeleteConfirmModal } from '../components/vocabulary/DeleteConfirmModal';
import { GroupFilterBar } from '../components/vocabulary/GroupFilterBar';
import { GroupModal } from '../components/vocabulary/GroupModal';
import { DeleteGroupConfirmModal } from '../components/vocabulary/DeleteGroupConfirmModal';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorMessage } from '../components/common/ErrorMessage';

const VocabEmptyIcon = (): React.ReactElement => (
  <svg className="w-8 h-8 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const SearchEmptyIcon = (): React.ReactElement => (
  <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);

const FolderEmptyIcon = (): React.ReactElement => (
  <svg className="w-8 h-8 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
  </svg>
);

export function VocabularyPage(): React.ReactElement {
  const [items, setItems] = useState<Vocabulary[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'az' | 'za'>('newest');

  // Groups state
  const [groups, setGroups] = useState<VocabularyGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState<'create' | 'rename'>('create');
  const [editingGroup, setEditingGroup] = useState<VocabularyGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<VocabularyGroup | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states for vocabulary CRUD
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Vocabulary | null>(null);
  const [deletingItem, setDeletingItem] = useState<Vocabulary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [, startTransition] = useTransition();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Determine backend sorting query
  const getSortParams = useCallback(() => {
    switch (sortOption) {
      case 'oldest':
        return { sortBy: 'createdAt' as const, sortOrder: 'asc' as const };
      case 'az':
        return { sortBy: 'word' as const, sortOrder: 'asc' as const };
      case 'za':
        return { sortBy: 'word' as const, sortOrder: 'desc' as const };
      case 'newest':
      default:
        return { sortBy: 'createdAt' as const, sortOrder: 'desc' as const };
    }
  }, [sortOption]);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const res = await vocabularyGroupService.getGroups();
      setGroups(res);
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  }, []);

  // Fetch vocabulary items
  const fetchVocabularies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { sortBy, sortOrder } = getSortParams();
      const res = await vocabularyService.getVocabularies({
        page,
        limit,
        search: debouncedSearch.trim() || undefined,
        sortBy,
        sortOrder,
        groupId: selectedGroupId === 'all' ? undefined : selectedGroupId,
      });
      startTransition(() => {
        setItems(res.items);
        setTotal(res.pagination.total);
        setTotalPages(res.pagination.totalPages);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vocabulary.');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, debouncedSearch, getSortParams, selectedGroupId]);

  useEffect(() => {
    void fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    void fetchVocabularies();
  }, [fetchVocabularies]);

  // Group Handlers
  const handleSelectGroup = (groupId: string): void => {
    setSelectedGroupId(groupId);
    setPage(1);
  };

  const handleOpenCreateGroup = (): void => {
    setGroupModalMode('create');
    setEditingGroup(null);
    setIsGroupModalOpen(true);
  };

  const handleOpenRenameGroup = (group: VocabularyGroup): void => {
    setGroupModalMode('rename');
    setEditingGroup(group);
    setIsGroupModalOpen(true);
  };

  const handleOpenDeleteGroup = (group: VocabularyGroup): void => {
    setDeletingGroup(group);
  };

  const handleGroupModalSubmit = async (name: string): Promise<void> => {
    if (groupModalMode === 'create') {
      await vocabularyGroupService.createGroup(name);
    } else if (editingGroup) {
      await vocabularyGroupService.updateGroup(editingGroup.id, name);
    }
    await fetchGroups();
    await fetchVocabularies();
  };

  const handleConfirmDeleteGroup = async (): Promise<void> => {
    if (!deletingGroup) return;
    setIsDeletingGroup(true);
    try {
      await vocabularyGroupService.deleteGroup(deletingGroup.id);
      if (selectedGroupId === deletingGroup.id) {
        setSelectedGroupId('all');
        setPage(1);
      }
      setDeletingGroup(null);
      await fetchGroups();
      await fetchVocabularies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete group.');
    } finally {
      setIsDeletingGroup(false);
    }
  };

  // Vocabulary Handlers
  const handleOpenAdd = (): void => {
    setEditingItem(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (vocab: Vocabulary): void => {
    setEditingItem(vocab);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: CreateVocabularyDTO): Promise<void> => {
    if (editingItem) {
      await vocabularyService.updateVocabulary(editingItem.id, data as UpdateVocabularyDTO);
    } else {
      await vocabularyService.createVocabulary(data);
    }
    await Promise.all([fetchVocabularies(), fetchGroups()]);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deletingItem) return;
    setIsDeleting(true);
    try {
      await vocabularyService.deleteVocabulary(deletingItem.id);
      setDeletingItem(null);
      await Promise.all([fetchVocabularies(), fetchGroups()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete vocabulary.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-in pb-12">
      {/* Top Header & Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white tracking-tight">My Vocabulary</h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-500/10 text-primary-400 border border-primary-500/20">
              {total} {total === 1 ? 'word' : 'words'}
            </span>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Build and manage your personalized IELTS vocabulary bank with collections and groups.
          </p>
        </div>

        <Button
          id="add-vocab-btn"
          variant="primary"
          onClick={handleOpenAdd}
          className="flex items-center gap-2 self-start sm:self-auto shrink-0 shadow-lg shadow-primary-900/30"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          <span>Add Vocabulary</span>
        </Button>
      </div>

      {/* Vocabulary Groups Bar */}
      <GroupFilterBar
        groups={groups}
        selectedGroupId={selectedGroupId}
        totalCount={total}
        onSelectGroup={handleSelectGroup}
        onOpenCreateModal={handleOpenCreateGroup}
        onOpenRenameModal={handleOpenRenameGroup}
        onOpenDeleteModal={handleOpenDeleteGroup}
        isLoading={isLoading}
      />

      {/* Filter and Search Bar */}
      <div className="glass-card p-4 rounded-2xl border-white/8 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <svg
            className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            id="vocab-search-input"
            type="text"
            placeholder="Search words or Vietnamese meanings…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-9 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
          />
          {search && (
            <button
              id="clear-search-btn"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              title="Clear search"
            >
              &times;
            </button>
          )}
        </div>

        {/* Sort Selector */}
        <div className="flex items-center gap-2 shrink-0">
          <label htmlFor="vocab-sort-select" className="text-xs font-medium text-slate-400 hidden sm:inline">
            Sort by:
          </label>
          <select
            id="vocab-sort-select"
            value={sortOption}
            onChange={(e) => {
              setSortOption(e.target.value as 'newest' | 'oldest' | 'az' | 'za');
              setPage(1);
            }}
            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
          >
            <option value="newest" className="bg-surface-900 text-white">Newest first</option>
            <option value="oldest" className="bg-surface-900 text-white">Oldest first</option>
            <option value="az" className="bg-surface-900 text-white">Alphabetical (A-Z)</option>
            <option value="za" className="bg-surface-900 text-white">Alphabetical (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="flex items-center justify-between p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <ErrorMessage message={error} />
          <Button variant="ghost" size="sm" onClick={() => void fetchVocabularies()}>
            Retry
          </Button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20">
          <LoadingSpinner size="lg" label="Loading your vocabulary bank…" />
        </div>
      )}

      {/* Empty states */}
      {!isLoading && !error && items.length === 0 && (
        <div className="py-8">
          {debouncedSearch ? (
            <EmptyState
              title="No matching words found"
              description={`No vocabulary items match "${debouncedSearch}". Try another keyword or clear your filter.`}
              icon={<SearchEmptyIcon />}
              action={{
                label: 'Clear Search',
                onClick: () => setSearch(''),
              }}
            />
          ) : selectedGroupId !== 'all' ? (
            <EmptyState
              title={selectedGroupId === 'ungrouped' ? 'No ungrouped words' : 'No words in this group'}
              description={
                selectedGroupId === 'ungrouped'
                  ? 'All your vocabulary items are currently assigned to one or more groups.'
                  : 'No vocabulary items have been added to this group yet. Edit an existing word or add a new word to assign it here.'
              }
              icon={<FolderEmptyIcon />}
              action={{
                label: 'View All Vocabulary',
                onClick: () => {
                  setSelectedGroupId('all');
                  setPage(1);
                },
              }}
            />
          ) : (
            <EmptyState
              title="No vocabulary yet"
              description="Start building your personal IELTS vocabulary bank with meanings, pronunciations, synonyms, and collocations."
              icon={<VocabEmptyIcon />}
              action={{
                label: 'Add Your First Word',
                onClick: handleOpenAdd,
              }}
            />
          )}
        </div>
      )}

      {/* Vocabulary Cards Grid */}
      {!isLoading && !error && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <VocabularyCard
              key={item.id}
              vocabulary={item}
              onEdit={handleOpenEdit}
              onDelete={(vocab) => setDeletingItem(vocab)}
            />
          ))}
        </div>
      )}

      {/* Pagination controls */}
      {!isLoading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-white/8 text-xs text-slate-400">
          <div>
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} words
          </div>
          <div className="flex items-center gap-2">
            <Button
              id="vocab-prev-page-btn"
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span className="px-2 font-medium text-white">
              Page {page} of {totalPages}
            </span>
            <Button
              id="vocab-next-page-btn"
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add / Edit Form Modal */}
      <VocabularyFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        initialData={editingItem}
        availableGroups={groups}
      />

      {/* Delete Confirmation Modal for Vocabulary */}
      <DeleteConfirmModal
        isOpen={Boolean(deletingItem)}
        vocabulary={deletingItem}
        onClose={() => setDeletingItem(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      {/* Create / Rename Group Modal */}
      <GroupModal
        isOpen={isGroupModalOpen}
        mode={groupModalMode}
        initialName={editingGroup?.name || ''}
        onClose={() => {
          setIsGroupModalOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleGroupModalSubmit}
      />

      {/* Delete Group Confirmation Modal */}
      <DeleteGroupConfirmModal
        isOpen={Boolean(deletingGroup)}
        group={deletingGroup}
        onClose={() => setDeletingGroup(null)}
        onConfirm={handleConfirmDeleteGroup}
        isDeleting={isDeletingGroup}
      />
    </div>
  );
}
